import {NumberGroupGranularityType} from './enum/NumberGroupGranularityType'
import {IGroupFilter} from '../../../types/IGroupFIlter'
import {buildNumberHasValueExpression} from '../../../functions/buildPropertyValueSortStages'

export interface INumberBin {
  from: number | null
  to: number | null
}

export const NUMBER_BIN_SIZE_BY_GRANULARITY: Record<NumberGroupGranularityType, number | null> = {
  [NumberGroupGranularityType.Exact]: null,
  [NumberGroupGranularityType.Bin1]: 1,
  [NumberGroupGranularityType.Bin10]: 10,
  [NumberGroupGranularityType.Bin100]: 100,
  [NumberGroupGranularityType.Bin1000]: 1000,
  [NumberGroupGranularityType.Custom]: null,
}

export const MAX_NUMBER_BIN_EDGES = 20

const isBinEdge = (value: unknown): value is number | null => value === null || typeof value === 'number'

export function isNumberBin(value: unknown): value is INumberBin {
  if(!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const bin = value as Partial<INumberBin>
  return isBinEdge(bin.from) && isBinEdge(bin.to) && (bin.from !== null || bin.to !== null)
}

export function toBucketNumber(value: unknown): number | null {
  if(typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if(!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  const parsed = Number(String(value))
  return Number.isFinite(parsed) ? parsed : null
}

export function bucketNumberByBinSize(value: unknown, size: number): number | null {
  if(typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }
  return Math.floor(value / size) * size || 0
}

export function buildNumberBin(from: number, size: number): INumberBin {
  return {
    from,
    to: from + size,
  }
}

export function normalizeNumberBinEdges(edges: unknown): number[] {
  if(!Array.isArray(edges)) {
    return []
  }
  const finiteEdges = edges.filter((edge): edge is number => typeof edge === 'number' && Number.isFinite(edge))
  return Array.from(new Set(finiteEdges))
    .sort((a, b) => a - b)
    .slice(0, MAX_NUMBER_BIN_EDGES)
}

export function bucketNumberByEdges(value: unknown, edges: number[]): number | null {
  if(typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }
  const index = edges.findIndex(edge => value < edge)
  return index === -1 ? edges.length : index
}

export function buildNumberBinFromEdges(index: number, edges: number[]): INumberBin {
  return {
    from: edges[index - 1] ?? null,
    to: edges[index] ?? null,
  }
}

export function buildNumberBinBucketExpression(valuePath: string, size: number): Record<string, unknown> {
  return {
    $cond: [
      buildNumberHasValueExpression(valuePath),
      {$multiply: [{$floor: {$divide: [`$${valuePath}`, size]}}, size]},
      null,
    ],
  }
}

export function buildNumberBinEdgesBucketExpression(valuePath: string, edges: number[]): Record<string, unknown> {
  return {
    $cond: [
      buildNumberHasValueExpression(valuePath),
      {
        $switch: {
          branches: edges.map((edge, index) => ({
            case: {$lt: [`$${valuePath}`, edge]},
            then: index,
          })),
          default: edges.length,
        },
      },
      null,
    ],
  }
}

export function buildNumberBinGroupFilter(valuePath: string, bin: INumberBin): IGroupFilter {
  return {
    [valuePath]: {
      ...(bin.from !== null ? {$gte: bin.from} : {}),
      ...(bin.to !== null ? {$lt: bin.to} : {}),
    },
  }
}
